import mammoth from "mammoth";
import { PDFDocument } from "pdf-lib";

// Images and Word files are turned into an image-only PDF in the browser so
// they can go through the same redaction editor as a PDF. The redacted copy
// is then flattened and copy-protected like any other (see redactionUtils).

const A4 = { width: 595.28, height: 841.89 }; // points
const DOCX_PAGE_PX = { width: 794, height: 1123 }; // A4 at 96 DPI
const DOCX_MARGIN_PX = 72;
const DOCX_SCALE = 2;

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp)$/i;
const DOCX_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export const isPdfFile = (file) =>
  file?.type === "application/pdf" || /\.pdf$/i.test(file?.name || "");

const isImageFile = (file) =>
  (/^image\/(png|jpeg|gif|webp|bmp)$/.test(file?.type || "") ||
    IMAGE_EXT.test(file?.name || "")) &&
  !/\.tiff?$/i.test(file?.name || "");

const isDocxFile = (file) =>
  file?.type === DOCX_TYPE || /\.docx$/i.test(file?.name || "");

// Files the "Redact & Upload" flow can open.
export const canRedactFile = (file) =>
  isPdfFile(file) || isImageFile(file) || isDocxFile(file);

const canvasToJpeg = (canvas) =>
  new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) =>
        blob
          ? blob.arrayBuffer().then(resolve, reject)
          : reject(new Error("Could not encode page image")),
      "image/jpeg",
      0.92,
    ),
  );

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const src = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(src);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(src);
      reject(new Error("Could not open this image."));
    };
    img.src = src;
  });
}

let html2canvasPromise = null;
function loadHtml2Canvas() {
  if (window.html2canvas) return Promise.resolve(window.html2canvas);
  if (!html2canvasPromise) {
    html2canvasPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src =
        "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
      s.onload = () => resolve(window.html2canvas);
      s.onerror = () => {
        html2canvasPromise = null;
        reject(new Error("Could not load the document renderer."));
      };
      document.head.appendChild(s);
    });
  }
  return html2canvasPromise;
}

// One page per image, fitted to A4 width.
async function imageToPdf(file) {
  const img = await loadImage(file);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);

  const out = await PDFDocument.create();
  const jpg = await out.embedJpg(await canvasToJpeg(canvas));
  canvas.width = 0;
  canvas.height = 0;
  const width = A4.width;
  const height = (img.naturalHeight / img.naturalWidth) * width;
  out.addPage([width, height]).drawImage(jpg, { x: 0, y: 0, width, height });
  return out.save();
}

// Picks where to end a page: the lowest fully white row in the bottom part
// of the slice, so a line of text isn't cut in half.
function findPageBreak(ctx, width, start, maxHeight, total) {
  const end = Math.min(start + maxHeight, total);
  if (end >= total) return total;
  const lookBack = Math.floor(maxHeight * 0.15);
  const { data } = ctx.getImageData(0, end - lookBack, width, lookBack);
  for (let row = lookBack - 1; row >= 0; row--) {
    let blank = true;
    for (let x = 0; x < width && blank; x += 2) {
      const i = (row * width + x) * 4;
      if (data[i] < 245 || data[i + 1] < 245 || data[i + 2] < 245) blank = false;
    }
    if (blank) return end - lookBack + row + 1;
  }
  return end;
}

// Renders the Word file to HTML (mammoth), draws it to a canvas and cuts it
// into A4 pages.
async function docxToPdf(file) {
  const [html2canvas, { value: html }] = await Promise.all([
    loadHtml2Canvas(),
    mammoth.convertToHtml({ arrayBuffer: await file.arrayBuffer() }),
  ]);

  const host = document.createElement("div");
  host.style.cssText = `position:fixed;left:-10000px;top:0;width:${DOCX_PAGE_PX.width}px;padding:${DOCX_MARGIN_PX}px;box-sizing:border-box;background:#fff;color:#000;font-family:"Times New Roman",serif;font-size:12pt;line-height:1.4;word-wrap:break-word;`;
  host.innerHTML = html;
  host.querySelectorAll("img").forEach((img) => {
    img.style.maxWidth = "100%";
  });
  host.querySelectorAll("table").forEach((t) => {
    t.style.borderCollapse = "collapse";
    t.style.width = "100%";
  });
  host.querySelectorAll("td, th").forEach((c) => {
    c.style.border = "1px solid #999";
    c.style.padding = "4px";
  });
  document.body.appendChild(host);

  let full;
  try {
    full = await html2canvas(host, {
      scale: DOCX_SCALE,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
    });
  } finally {
    host.remove();
  }

  const out = await PDFDocument.create();
  const fullCtx = full.getContext("2d");
  const pageH = DOCX_PAGE_PX.height * DOCX_SCALE;
  const marginH = DOCX_MARGIN_PX * DOCX_SCALE;
  const page = document.createElement("canvas");
  page.width = full.width;
  page.height = pageH;
  const pageCtx = page.getContext("2d");

  try {
    // The first slice already carries the top margin from the padding; later
    // slices get a top margin added, and every slice leaves a bottom margin.
    let y = 0;
    while (y < full.height) {
      const top = y === 0 ? 0 : marginH;
      const room = pageH - top - marginH;
      const end = findPageBreak(fullCtx, full.width, y, room, full.height);
      pageCtx.fillStyle = "#ffffff";
      pageCtx.fillRect(0, 0, page.width, page.height);
      pageCtx.drawImage(full, 0, y, full.width, end - y, 0, top, full.width, end - y);
      const jpg = await out.embedJpg(await canvasToJpeg(page));
      out
        .addPage([A4.width, A4.height])
        .drawImage(jpg, { x: 0, y: 0, width: A4.width, height: A4.height });
      y = end;
    }
  } finally {
    full.width = 0;
    full.height = 0;
    page.width = 0;
    page.height = 0;
  }
  return out.save();
}

/** Returns the file as PDF bytes (Uint8Array), converting images and DOCX. */
export async function fileToPdfBytes(file) {
  if (isPdfFile(file)) return new Uint8Array(await file.arrayBuffer());
  if (isImageFile(file)) return imageToPdf(file);
  if (isDocxFile(file)) return docxToPdf(file);
  throw new Error("This file type can't be redacted.");
}
